import React from "react";
import { API_BASE_URL } from "./apiConfig";
import { getTermsAccepted } from "./auth";
import { preloadImage } from "./imagePreload";
import { apiClient } from "./apiClient";

/**
 * Gets the profile picture URL for the current authenticated user
 */
export function getMyAvatarUrl(): string {
    return `${API_BASE_URL}/api/profile/me/avatar`;
}

/**
 * Gets the profile picture URL for a specific user by userId (admin only, same company)
 * This is the preferred method when userId is available as it's more efficient
 */
export function getUserAvatarUrlByUserId(userId: number): string {
    return `${API_BASE_URL}/api/profile/me/avatar?userId=${userId}`;
}

/**
 * Gets the current user's ID from localStorage
 */
export function getCurrentUserName(): string | null {
    try {
        // Get from localStorage (set during login/OAuth callback)
        const storageUserId = localStorage.getItem("loggedInUserId") || localStorage.getItem("userId");
        if (storageUserId) {
            return storageUserId;
        }

        return null;
    } catch (error) {
        return null;
    }
}

// -------- Shared URL helper (null-safe) --------

/** Absolutize /uploads/... (or bare uploads/...) to the backend host */
export function toAbsoluteUrl(u?: string | null): string | undefined {
    if (typeof u !== "string") return undefined;
    const raw = u.trim();
    if (!raw) return undefined;

    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith("//")) return `http:${raw}`;
    if (/^(data:|blob:)/i.test(raw)) return raw;

    if (raw.startsWith("/")) return `${API_BASE_URL}${raw}`;
    if (raw.toLowerCase().startsWith("uploads/")) return `${API_BASE_URL}/${raw}`;
    return `${API_BASE_URL}/${raw}`;
}


/**
 * Uploads a profile picture for a user
 * @param file - The image file to upload
 * @param userId - Optional userId for admin uploads (when editing team member)
 */
export async function uploadUserAvatar(file: File, userId?: number): Promise<{ success: boolean; error?: string }> {
    try {
        // Create FormData with only the file
        const formData = new FormData();
        formData.append('file', file);

        // Build request URL with userId parameter if provided (for admin uploads)
        let requestUrl = `${API_BASE_URL}/api/profile/me/avatar`;
        if (userId !== undefined) {
            requestUrl += `?userId=${userId}`;
        }

        const response = await apiClient(requestUrl, {
            method: 'POST',
            body: formData,
            credentials: 'include', // Send cookies automatically
        });

        if (!response.ok) {
            const errorText = await response.text();
            return {
                success: false,
                error: `Upload failed: ${response.status} - ${errorText}`
            };
        }

        return { success: true };
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Upload failed'
        };
    }
}

/**
 * Uploads a company logo for the authenticated administrator's company.
 */
export async function uploadCompanyLogo(file: File): Promise<{ success: boolean; error?: string }> {
    try {
        const formData = new FormData();
        formData.append('file', file);

        const requestUrl = `${API_BASE_URL}/api/company/me/logo`;

        const response = await apiClient(requestUrl, {
            method: 'POST',
            body: formData,
            credentials: 'include', // Send cookies automatically
        });

        if (!response.ok) {
            const errorText = await response.text();
            return {
                success: false,
                error: `Upload failed: ${response.status} - ${errorText}`
            };
        }

        return { success: true };
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Upload failed'
        };
    }
}

// Cache for ongoing requests to prevent duplicate fetches
let ongoingAvatarRequest: Promise<string | null> | null = null;
let cachedAvatarUrl: string | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 30000; // Increased to 30 seconds to reduce flickering

// Track active listeners to prevent cleanup while still in use
let activeAvatarListeners = 0;

/**
 * Clears the current user's avatar cache
 */
export function clearMyAvatarCache(): void {
    if (cachedAvatarUrl) {
        URL.revokeObjectURL(cachedAvatarUrl);
    }
    cachedAvatarUrl = null;
    cacheTimestamp = 0;
    ongoingAvatarRequest = null;
}

/**
 * Fetches the current user's profile picture as a blob URL with deduplication
 * @param bustCache - If true, adds timestamp to URL to force fresh fetch
 */
export async function fetchMyAvatarBlobUrl(bustCache: boolean = false): Promise<string | null> {
    // Check if terms have been accepted before making API calls
    const hasAcceptedTerms = getTermsAccepted();
    if (!hasAcceptedTerms) {
        return null;
    }

    // Return cached result if still valid and not busting cache
    const now = Date.now();
    if (!bustCache && cachedAvatarUrl && (now - cacheTimestamp) < CACHE_DURATION) {
        return cachedAvatarUrl;
    }

    // If there's already an ongoing request, return that promise
    if (ongoingAvatarRequest) {
        return ongoingAvatarRequest;
    }

    // Create new request
    ongoingAvatarRequest = (async () => {
        try {
            // Add cache-busting timestamp when requested
            let apiUrl = getMyAvatarUrl();
            if (bustCache) {
                apiUrl += `?_t=${Date.now()}`;
            }

            const response = await apiClient(apiUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'image/*',
                },
                credentials: 'include', // Send cookies automatically
                cache: 'no-cache',
            });

            if (!response.ok) {
                if (response.status === 404) {
                    return null;
                }
                return null;
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            // Update cache
            cachedAvatarUrl = blobUrl;
            cacheTimestamp = Date.now();

            return blobUrl;
        } catch (error) {
            return null;
        } finally {
            // Clear the ongoing request
            ongoingAvatarRequest = null;
        }
    })();

    return ongoingAvatarRequest;
}

// Cache for user avatars (keyed by username)
const userAvatarCache: Map<string, { url: string | null; timestamp: number }> = new Map();
const ongoingUserAvatarRequests: Map<string, Promise<string | null>> = new Map();

// Cache for company logos (keyed by username)
const companyLogoCache: Map<string, { url: string | null; timestamp: number }> = new Map();
const ongoingCompanyLogoRequests: Map<string, Promise<string | null>> = new Map();

/**
 * Clears the user avatar cache for a specific user or all users
 */
export function clearUserAvatarCache(userId?: number): void {
    if (userId !== undefined) {
        const cacheKey = `userId:${userId}`;
        const cached = userAvatarCache.get(cacheKey);
        if (cached?.url) {
            URL.revokeObjectURL(cached.url);
        }
        userAvatarCache.delete(cacheKey);
        ongoingUserAvatarRequests.delete(cacheKey);
    } else {
        // Clear all user avatar caches
        userAvatarCache.forEach(cached => {
            if (cached.url) {
                URL.revokeObjectURL(cached.url);
            }
        });
        userAvatarCache.clear();
        ongoingUserAvatarRequests.clear();
    }
}

/** Revoke a blob URL and drop it from the shared cache so remounts refetch. */
function revokeAndEvictUserAvatarBlobUrl(userId: number, blobUrl: string): void {
    const cacheKey = `userId:${userId}`;
    const cached = userAvatarCache.get(cacheKey);
    if (cached?.url === blobUrl) {
        userAvatarCache.delete(cacheKey);
        ongoingUserAvatarRequests.delete(cacheKey);
    }
    URL.revokeObjectURL(blobUrl);
}

/**
 * Clears the company logo cache for a specific user or all users
 */
function revokeCachedCompanyLogo(cacheKey: string): void {
    const cached = companyLogoCache.get(cacheKey);
    if (cached?.url) {
        URL.revokeObjectURL(cached.url);
    }
}

export function clearCompanyLogoCache(username?: string): void {
    if (username) {
        revokeCachedCompanyLogo(username);
        companyLogoCache.delete(username);
        ongoingCompanyLogoRequests.delete(username);
    } else {
        companyLogoCache.forEach((cached, key) => {
            if (cached.url) {
                URL.revokeObjectURL(cached.url);
            }
            ongoingCompanyLogoRequests.delete(key);
        });
        companyLogoCache.clear();
        ongoingCompanyLogoRequests.clear();
    }
}

/** Prefer presigned API URL (correct Content-Type); blob from /api/company/me/logo is fallback. */
export function resolveCompanyLogoDisplayUrl(
    presignedUrl?: string | null,
    blobUrl?: string | null
): string | null {
    return toAbsoluteUrl(presignedUrl) ?? blobUrl ?? null;
}

/**
 * Fetches a specific user's profile picture as a blob URL
 * @param userId - User's numeric ID
 * @param bustCache - Force a fresh fetch ignoring cache
 */
export async function fetchUserAvatarBlobUrl(
    userId: number,
    bustCache: boolean = false
): Promise<string | null> {
    const cacheKey = `userId:${userId}`;
    const now = Date.now();

    // Return cached result if still valid and not busting cache
    if (!bustCache) {
        const cached = userAvatarCache.get(cacheKey);
        if (cached && (now - cached.timestamp) < CACHE_DURATION) {
            return cached.url;
        }
    }

    // If there's already an ongoing request for this user, return that promise
    const ongoingRequest = ongoingUserAvatarRequests.get(cacheKey);
    if (ongoingRequest) {
        return ongoingRequest;
    }

    // Create new request
    const request = (async () => {
        try {
            // Build URL with userId parameter
            let url = getUserAvatarUrlByUserId(userId);
            // Add cache-busting timestamp when requested
            if (bustCache) {
                url += `&_t=${Date.now()}`;
            }

            const response = await apiClient(url, {
                method: 'GET',
                headers: {
                    'Accept': 'image/*',
                },
                credentials: 'include', // Send cookies automatically
                cache: 'no-cache',
            });

            if (!response.ok) {
                if (response.status === 404) {
                    userAvatarCache.set(cacheKey, { url: null, timestamp: now });
                    return null;
                }
                if (response.status === 403) {
                    // User lacks permission - cache the result to avoid repeated failed requests
                    console.warn('fetchUserAvatarBlobUrl: Permission denied (403) for userId:', userId);
                    userAvatarCache.set(cacheKey, { url: null, timestamp: now });
                    return null;
                }
                return null;
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            // Update cache
            userAvatarCache.set(cacheKey, { url: blobUrl, timestamp: now });

            return blobUrl;
        } catch (error) {
            return null;
        } finally {
            // Clear the ongoing request
            ongoingUserAvatarRequests.delete(cacheKey);
        }
    })();

    ongoingUserAvatarRequests.set(cacheKey, request);
    return request;
}

/**
 * Fetches company logo and returns a blob URL
 */
export async function fetchCompanyLogoBlobUrl(username: string, bustCache: boolean = false): Promise<string | null> {
    const cacheKey = username;
    const now = Date.now();

    if (bustCache) {
        revokeCachedCompanyLogo(cacheKey);
        companyLogoCache.delete(cacheKey);
        ongoingCompanyLogoRequests.delete(cacheKey);
    }

    // Return cached result if still valid and not busting cache
    if (!bustCache) {
        const cached = companyLogoCache.get(cacheKey);
        if (cached && (now - cached.timestamp) < CACHE_DURATION) {
            return cached.url;
        }
    }

    // If there's already an ongoing request for this user, return that promise
    const ongoingRequest = ongoingCompanyLogoRequests.get(cacheKey);
    if (ongoingRequest && !bustCache) {
        return ongoingRequest;
    }

    // Create new request
    const request = (async () => {
        try {
            const url = `${API_BASE_URL}/api/company/me/logo`;
            // NOTE: Removed cache-busting query parameter as it causes backend 500 errors
            // Relying on cache: 'no-cache' header instead

            const response = await apiClient(url, {
                method: 'GET',
                headers: {
                    'Accept': 'image/*',
                },
                credentials: 'include', // Send cookies automatically
                cache: 'no-cache',
            });

            if (!response.ok) {
                if (response.status === 404) {
                    // Cache the 404 result
                    companyLogoCache.set(cacheKey, { url: null, timestamp: now });
                    return null;
                }
                return null;
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            // Update cache
            companyLogoCache.set(cacheKey, { url: blobUrl, timestamp: now });

            return blobUrl;
        } catch (error) {
            return null;
        } finally {
            // Clear the ongoing request
            ongoingCompanyLogoRequests.delete(cacheKey);
        }
    })();

    ongoingCompanyLogoRequests.set(cacheKey, request);
    return request;
}

/**
 * Hook for using profile picture in React components
 * @param shouldFetch - Whether to fetch the avatar. If false, returns null immediately.
 */
export const useMyAvatar = (shouldFetch: boolean = true) => {
    const [avatarUrl, setAvatarUrl] = React.useState<string | null>(null);

    // Create a stable refresh function
    const refreshAvatar = React.useCallback(async () => {
        if (!shouldFetch) {
            setAvatarUrl(null);
            return;
        }

        try {
            // Bust cache when explicitly refreshing
            cachedAvatarUrl = null;
            cacheTimestamp = 0;
            const url = await fetchMyAvatarBlobUrl(true);
            if (url) {
                await preloadImage(url);
            }
            setAvatarUrl(url);
        } catch (error) {
            console.error('useMyAvatar: Error in refreshAvatar', error);
            setAvatarUrl(null);
        }
    }, [shouldFetch]);

    // Load avatar only once on mount
    React.useEffect(() => {
        if (!shouldFetch) {
            setAvatarUrl(null);
            return;
        }

        let isMounted = true;
        activeAvatarListeners++;

        const loadAvatar = async () => {
            if (!isMounted) return;

            try {
                const url = await fetchMyAvatarBlobUrl(false);
                if (url) {
                    await preloadImage(url);
                }
                if (isMounted) {
                    setAvatarUrl(url);
                }
            } catch (error) {
                console.error('useMyAvatar: Error loading avatar', error);
                if (isMounted) {
                    setAvatarUrl(null);
                }
            }
        };

        loadAvatar();

        return () => {
            isMounted = false;
            activeAvatarListeners--;

            // Only cleanup blob URL if there are no more active listeners
            if (activeAvatarListeners === 0 && cachedAvatarUrl) {
                URL.revokeObjectURL(cachedAvatarUrl);
                cachedAvatarUrl = null;
                cacheTimestamp = 0;
            }
        };
    }, [shouldFetch]); // Re-run when shouldFetch changes

    // Listen for profile picture update events
    React.useEffect(() => {
        if (!shouldFetch) {
            return;
        }

        const handleProfilePictureUpdate = () => {
            refreshAvatar();
        };

        window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate);
        window.addEventListener('athleteProfileUpdated', handleProfilePictureUpdate);

        return () => {
            window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate);
            window.removeEventListener('athleteProfileUpdated', handleProfilePictureUpdate);
        };
    }, [refreshAvatar, shouldFetch]);

    return React.useMemo(() => ({
        avatarUrl,
        loading: false,
        refreshAvatar
    }), [avatarUrl, refreshAvatar]);
};

/**
 * Hook for using a specific user's profile picture in React components
 * @param userId - User's numeric ID (required)
 */
export const useUserAvatar = (userId?: number) => {
    const [avatarUrl, setAvatarUrl] = React.useState<string | null>(null);

    // Create a stable refresh function that busts cache
    const refreshAvatar = React.useCallback(async () => {
        if (!userId) {
            setAvatarUrl(null);
            return;
        }

        try {
            const url = await fetchUserAvatarBlobUrl(userId, true);
            if (url) {
                await preloadImage(url);
            }
            setAvatarUrl(prevUrl => {
                // Clean up previous URL if it's different
                if (prevUrl && prevUrl !== url) {
                    URL.revokeObjectURL(prevUrl);
                }
                return url;
            });
        } catch (error) {
            console.error('useUserAvatar: Error refreshing avatar', error);
            setAvatarUrl(null);
        }
    }, [userId]);

    // Load avatar when userId changes
    React.useEffect(() => {
        let isMounted = true;

        const loadAvatar = async () => {
            if (!userId) {
                if (isMounted) {
                    setAvatarUrl(null);
                }
                return;
            }

            // Clear previous avatar immediately when userId changes
            setAvatarUrl(prevUrl => {
                if (prevUrl) {
                    URL.revokeObjectURL(prevUrl);
                }
                return null;
            });

            try {
                const url = await fetchUserAvatarBlobUrl(userId, false);
                if (url) {
                    await preloadImage(url);
                }
                if (isMounted) {
                    setAvatarUrl(url);
                }
            } catch (error) {
                console.error('useUserAvatar: Error loading avatar for userId:', userId, error);
                if (isMounted) {
                    setAvatarUrl(null);
                }
            }
        };

        loadAvatar();

        return () => {
            isMounted = false;
            // Clean up blob URL on unmount and evict cache so remount refetches
            if (userId !== undefined) {
                setAvatarUrl(prevUrl => {
                    if (prevUrl) {
                        revokeAndEvictUserAvatarBlobUrl(userId, prevUrl);
                    }
                    return null;
                });
            }
        };
    }, [userId]); // Re-run whenever userId changes

    // Listen for profile update events to refresh avatar
    React.useEffect(() => {
        const handleProfileUpdate = () => {
            if (userId) {
                refreshAvatar();
            }
        };

        window.addEventListener('athleteProfileUpdated', handleProfileUpdate);
        window.addEventListener('profileUpdated', handleProfileUpdate);
        window.addEventListener('profilePictureUpdated', handleProfileUpdate);

        return () => {
            window.removeEventListener('athleteProfileUpdated', handleProfileUpdate);
            window.removeEventListener('profileUpdated', handleProfileUpdate);
            window.removeEventListener('profilePictureUpdated', handleProfileUpdate);
        };
    }, [userId, refreshAvatar]);

    return { avatarUrl, loading: false, refreshAvatar };
};

/**
 * Hook for using company logo in React components
 */
export const useCompanyLogo = (username?: string) => {
    const [logoUrl, setLogoUrl] = React.useState<string | null>(null);

    // Create a stable refresh function that busts cache
    const refreshLogo = React.useCallback(async () => {
        if (!username) {
            setLogoUrl(null);
            return;
        }

        try {
            const url = await fetchCompanyLogoBlobUrl(username, true);
            setLogoUrl(prevUrl => {
                // Clean up previous URL if it's different
                if (prevUrl && prevUrl !== url) {
                    URL.revokeObjectURL(prevUrl);
                }
                return url;
            });
        } catch (error) {
            console.error('useCompanyLogo: Error refreshing logo', error);
            setLogoUrl(null);
        }
    }, [username]);

    // Load logo when username changes
    React.useEffect(() => {
        let isMounted = true;

        const loadLogo = async () => {
            if (!username || !isMounted) {
                if (isMounted) {
                    setLogoUrl(null);
                }
                return;
            }

            try {
                const url = await fetchCompanyLogoBlobUrl(username);
                if (isMounted) {
                    setLogoUrl(prevUrl => {
                        // Clean up previous URL if it's different
                        if (prevUrl && prevUrl !== url) {
                            URL.revokeObjectURL(prevUrl);
                        }
                        return url;
                    });
                }
            } catch (error) {
                if (isMounted) {
                    setLogoUrl(null);
                }
            }
        };

        loadLogo();

        return () => {
            // Clean up blob URL on unmount
            setLogoUrl(prevUrl => {
                if (prevUrl) {
                    URL.revokeObjectURL(prevUrl);
                }
                return null;
            });
        };
    }, [username]);

    return { logoUrl, loading: false, refreshLogo };
};
